import { compareDesc } from 'date-fns'
import { AssetCardProps } from 'features/assetDatabase'
import { useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDefaults,
  GeckoUnit,
} from 'types'

export const useAssetCardsData = (
  assetModels: ReadonlyArray<GeckoAssetModel>,
  units: ReadonlyArray<GeckoUnit>,
  components: ReadonlyArray<GeckoComponent>,
  cdfs: ReadonlyArray<GeckoComponentDataFrame>,
  componentDefaults: ReadonlyArray<GeckoComponentDefaults>
) => {
  return useMemo(() => {
    const result = new Array<AssetCardProps>()
    units.forEach((unit) => {
      // Filter and sort CDFs for this unit
      const unitCDFs = cdfs
        .filter((cdf) => cdf.tags.unit === unit.id)
        .sort((a, b) => {
          const dateA = a.data.params.date
          const dateB = b.data.params.date
          if (!dateA && !dateB) return 0
          if (!dateA) return 1 // Prefer a CDF with a date to one without
          if (!dateB) return -1
          return compareDesc(dateA, dateB) // Prefer more recent CDFs
        })

      const assetModel = assetModels.find(
        (assetModel) => assetModel.tags.unit === unit.id
      )

      const selectedCDF = unitCDFs.at(0)

      const selectedComponent =
        (selectedCDF
          ? components.find((c) => c.id === selectedCDF.tags.component)
          : components.find((c) => assetModel?.data.components.has(c.id))) ??
        components.find((c) => c.tags.unit === unit.id)

      const selectedComponentDefaults = selectedComponent
        ? componentDefaults.find(
            (defaults) => defaults.tags.component === selectedComponent.id
          )
        : undefined

      const colorThreshold =
        selectedComponentDefaults && selectedCDF
          ? selectedComponentDefaults.data.color_scale_thresholds.get(
              selectedCDF.data.params.view.id
            )
          : undefined

      // Only show units that have components, unless user has core:edit permissions (in which case, show all)
      if (selectedComponent) {
        result.push({
          unit: unit,
          assetModel: assetModel,
          component: selectedComponent,
          colorThreshold: colorThreshold,
          componentDataFrame: selectedCDF,
        })
      }
    })

    return result.sort((a, b) => GeckoUnit.DefaultSort(a.unit, b.unit))
  }, [assetModels, units, components, cdfs, componentDefaults])
}
